<template>
    <div class="h-full w-full flex flex-col gap-2 p-2">
        <dialog
            :open="!!validationError"
            class="bg-error text-white p-5 rounded"
        >
            <p v-if="!!validationError">{{ cachedT(validationError) }}</p>
        </dialog>
        <div class="flex w-full justify-between">
            <sub-account-header-actions ref="editor" class="ml-1" :is-customer="isCustomer" />
            <UInput
                v-model="searchText"
                type="search"
                :placeholder="props.isCustomer ? 'Søk etter kunde': 'Søk etter leverandør'"
                class="w-1/4"
                icon="i-ph-magnifying-glass"
                @update:model-value="applySearchText"
            />
        </div>
        <ag-grid-vue
            class="w-full h-full py-4"
            data-cy="sub-account-list"
            :theme="agTheme"
            :column-defs="columns"
            :row-data="sortedSubAccounts"
            :grid-options="gridOptions"
            edit-type="fullRow"
        />
    </div>
</template>

<script setup lang="ts">

import {computed, inject, PropType, Ref, ref, shallowRef} from 'vue'
import {SubAccount, SubAccount as TSubAccount} from '@/subaccount/types/SubAccount'
import {sortBy} from 'lodash'
import {AgGridVue} from 'ag-grid-vue3'
import SubAccountNameCell from '@/subaccount/components/SubAccountNameCell.vue'
import {ColDef, EditableCallbackParams, GridApi, GridOptions, GridReadyEvent, IRowNode, ValueGetterParams, ValueSetterParams} from 'ag-grid-community'
import Fuse from 'fuse.js'
import noRowsOverlayComponent from '@/components/NoRowsOverlayComponent.vue'
import {agTheme} from '@/agUtils/ag-theme.service'
import SubAccountIsBusinessCell from '@/subaccount/components/SubAccountIsBusinessCell.vue'
import {useSubAccountValidation} from '@/subaccount/subAccountValidator'
import SubAccountListActionsCell from '@/subaccount/components/SubAccountListActionsCell.vue'
import {formatCurrency} from '@/services/formatter.service'
import {fetchCustomers, fetchSuppliers, updateSubAccount} from '@/subaccount/subAccount.service'
import SubAccountHeaderActions from '@/subaccount/components/SubAccountHeaderActions.vue'
import {useCachedT} from '@/services/useCachedI18n'
import {globalDefaultColDef} from '@/agUtils'

const {cachedT} = useCachedT()
const gridApi = shallowRef<GridApi>()
const validationError = ref()

const editor = ref()
const searchText = ref('')
const panelId: Ref<number> | undefined = inject('panelId')
const validator = useSubAccountValidation()

const error = (message: string | undefined) => {
    if (message) {
        validationError.value = message
        setTimeout(() => {
            validationError.value = undefined
        }, 5000)
    }
    return !validationError.value
}

const props = defineProps({
    subAccounts: {
        type: Array as PropType<Array<TSubAccount>>,
        required: true
    },
    isCustomer: {
        type: Boolean,
        default: true
    }
})

const sortedSubAccounts = computed(() => {
    return sortBy(props.subAccounts, 'name')
})

const columns: ColDef[] = [
    {
        headerName: props.isCustomer ? 'Kundenr.' : 'Lev.nr.',
        field: 'subAccountNo',
        pinned: 'left',
        flex: 1,
        minWidth: 80,
        maxWidth: 80
    },
    {
        headerName: props.isCustomer ? 'Kunde' : 'Leverandør',
        field: 'name',
        comparator: (a: string, b: string) => {
            return a.toLowerCase().localeCompare(b.toLocaleLowerCase())
        },
        cellRenderer: SubAccountNameCell,
        cellRendererParams: {
            subAccount: props.isCustomer
        },
        editable: true,
        valueSetter: ({data, newValue}: ValueSetterParams) => {
            data.name = newValue
            updateAndRefresh(data)
            return true
        },
        pinned: 'left'
    },
    {
        headerName: 'Bedr. / priv.',
        field: 'isBusiness',
        cellRenderer: SubAccountIsBusinessCell,
        flex: 1
    },
    {
        headerName: 'Org.nr',
        field: 'orgNo',
        editable: ({data}: EditableCallbackParams) => data.isBusiness,
        valueGetter: ({data}: ValueGetterParams) => {
            data.isBusiness = data.orgNo > 0
            return data.orgNo
        },
        valueSetter: ({data, newValue}: ValueSetterParams) => {
            data.orgNo = newValue
            updateAndRefresh(data)
            return error(validator.validateOrgNo(data.orgNo, data.address?.postalCode, data.isBusiness))
        },
        flex: 1,
        minWidth: 80
    },
    {
        headerName: 'Gateadresse',
        field: 'address.addressLine',
        editable: true,
        flex: 1,
        minWidth: 80,
        valueSetter: ({data, newValue}: ValueSetterParams) => {
            data.address = {
                ...data.address,
                addressLine: newValue
            }
            updateAndRefresh(data)
            return true
        }
    },
    {
        headerName: 'Postnummer',
        field: 'address.postalCode',
        editable: true,
        valueSetter: ({data, newValue}: ValueSetterParams) => {
            data.address = {
                ...data.address,
                postalCode: newValue
            }
            updateAndRefresh(data)
            return true
        },
        flex: 1,
        minWidth: 80
    },
    {
        headerName: 'Poststed',
        field: 'address.postalLocation',
        editable: true,
        valueSetter: ({data, newValue}: ValueSetterParams) => {
            data.address = {
                ...data.address,
                postalLocation: newValue
            }
            updateAndRefresh(data)
            return true
        },
        flex: 1,
        minWidth: 80
    },
    {
        headerName: 'Kontaktperson',
        field: 'contactInfo.name',
        editable: true,
        valueSetter: ({data, newValue}: ValueSetterParams) => {
            data.contactInfo = {
                ...data.contactInfo,
                name: newValue
            }
            updateAndRefresh(data)
            return true
        },
        flex: 1,
        minWidth: 80
    },
    {
        headerName: 'Tlf.',
        field: 'contactInfo.phoneNumber',
        editable: true,
        valueSetter: ({data, newValue}: ValueSetterParams) => {
            data.contactInfo = {
                ...data.contactInfo,
                phoneNumber: newValue
            }
            updateAndRefresh(data)
            return true
        },
        hide: true,
        flex: 1,
        minWidth: 80
    },
    {
        headerName: 'Epost',
        field: 'contactInfo.email',
        editable: true,
        valueSetter: (params: ValueSetterParams) => {
            params.data.contactInfo.email = params.newValue
            if (error(validator.validateName(params.data.contactInfo.name)) || error(validator.validateEmail(params.data.contactInfo.email)))
                return false
            updateAndRefresh(params.data)
            return true
        },
        flex: 1,
        minWidth: 80
    },
    {
        headerName: 'Aktiv',
        field: 'active',
        editable: true,
        cellRenderer: 'agCheckboxCellRenderer',
        valueSetter: ({data, newValue}: ValueSetterParams) => {
            data.active = !!newValue
            updateAndRefresh(data)
            return true
        },
        flex: 1,
        minWidth: 80
    },
    {
        headerName: 'Sum restbeløp',
        field: 'totalUnpaid',
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        cellRenderer: ({value}: any) => formatCurrency(value || 0, 0),
        flex: 1,
        minWidth: 80
    },
    {
        headerName: props.isCustomer ? 'Sum fakturert' : 'Sum ordre',
        field: 'totalInvoiced',
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right'},
        cellRenderer: ({value}: any) => formatCurrency(value || 0, 0),
        flex: 1,
        minWidth: 80
    },
    {
        headerName: '',
        field: 'actions',
        cellRenderer: SubAccountListActionsCell,
        flex: 1,
        hide: !props.isCustomer,
        minWidth: 80
    },
    {
        headerName: 'Kostnadskonto',
        field: 'metadata.suggestions.expenseAccount',
        hide: props.isCustomer,
        editable: true,
        valueSetter: ({data, newValue}: ValueSetterParams) => {
            data.metadata = {
                ...data.metadata,
                suggestions: {
                    ...data.metadata.suggestions,
                    expenseAccount: newValue
                }
            }
            updateAndRefresh(data)
            return true
        }
    }

]

//TODO should include a search for org.no?
const filterRowsBySearch = (node: IRowNode) => {
    const subAccount = node.data
    if (searchText.value === '')
        return true

    const fuse = new Fuse([
        subAccount.name
    ], {includeScore: true, threshold: 0.3})
    return !!fuse.search(searchText.value).length
}

const applySearchText = () => {
    if (gridApi.value) {
        gridApi.value.onFilterChanged()
    }
}

const gridOptions: GridOptions = {
    suppressContextMenu: true,
    context: {
        panelId
    },
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
    },
    animateRows: true,
    noRowsOverlayComponent: noRowsOverlayComponent,
    noRowsOverlayComponentParams: {
        getMessage: () => `Ingen ${props.isCustomer ? 'kunder' : 'leverandører'}`
    },
    getRowId: (params: any) => params.data.uuid,
    isExternalFilterPresent: () => true,
    doesExternalFilterPass: filterRowsBySearch,
    suppressCellFocus: true,
    defaultColDef: {
        ...globalDefaultColDef,
        sortable: true,
        resizable: true
    },
}

const updateAndRefresh = async (updatedSubAcc: SubAccount) => {
    await updateSubAccount(updatedSubAcc)
    if (props.isCustomer) {
        await fetchCustomers()
    } else {
        await fetchSuppliers()
    }
}

</script>
